import {
  doc,
  updateDoc,
  setDoc,
  collection,
  arrayUnion,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { firestore, GeoFirestore } from "../../../firebase";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import languagesShortened from "../../../assets/assetComponents/languagesShortened";
import { generateUniqueSlug } from "../../../utils/slug";

const db = firestore;

const mapToken =
  "pk.eyJ1Ijoic3RlZmZlbmxpZTEiLCJhIjoiY2xqNDBzNnpyMDlxeTNlczFidW9seGxsOSJ9.DNY1zitSfG0YZ3SvjARdHg";

export const emptyOpeningHours = () => ({
  mon: { open: null, close: null },
  tue: { open: null, close: null },
  wed: { open: null, close: null },
  thu: { open: null, close: null },
  fri: { open: null, close: null },
  sat: { open: null, close: null },
  sun: { open: null, close: null },
});

export const emptyClosingHours = () => ({
  mon: null,
  tue: null,
  wed: null,
  thu: null,
  fri: null,
  sat: null,
  sun: null,
});

// opening_hours is the object from the Places Details result
export function convertGoogleOpeningHours(opening_hours) {
  // ✅ ALWAYS create base first (failsafe)
  const result = emptyOpeningHours();

  // ✅ If no opening hours exist → return all-null safely
  if (!opening_hours || !Array.isArray(opening_hours.periods)) {
    return result;
  }

  // Google: 0=Sun,1=Mon,2=Tue,3=Wed,4=Thu,5=Fri,6=Sat
  const dayMap = {
    0: "sun",
    1: "mon",
    2: "tue",
    3: "wed",
    4: "thu",
    5: "fri",
    6: "sat",
  };

  const parseTime = (hhmm) => ({
    hour: Number(hhmm.slice(0, 2)),
    minutes: Number(hhmm.slice(2, 4)),
  });

  opening_hours.periods.forEach((p) => {
    const dayKey = dayMap[p?.open?.day];

    if (!dayKey) return;

    if (p.open?.time) {
      result[dayKey].open = parseTime(p.open.time);
    }

    if (p.close?.time) {
      result[dayKey].close = parseTime(p.close.time);
    }
  });

  return result;
}

export async function fetchGoogleBusinessDetails(placeId) {
  const res = await fetch(
    `https://us-central1-menuapp-4c413.cloudfunctions.net/tripadvisor/googleBusinessDetails/${placeId}`,
  );
  const data = await res.json();
  return data.businessDetailsData.result;
}

/**
 * createLocation — the shared "create a new lokasjon" flow used by both
 * myLocations.js (onAddNewLocation) and quickAddLocation.js.
 *
 * Creates the geo doc, slug, map image, Google rating subcollection and adds
 * the id to the business's menus list. `extraFields(lang)` can return more
 * fields to write on the new doc (it gets the resolved language so callers
 * can build *Lang maps). Returns { id, lang, currency }.
 */
export async function createLocation({
  currentBusinessID,
  userInformation,
  locationName,
  locationType,
  locationSecondaryType,
  latitude,
  longitude,
  googleBusinessDetails,
  regularOpeningHours,
  regularClosingHours,
  extraFields,
}) {
  const userInfo = doc(db, "businesses", currentBusinessID);
  const geocollection = GeoFirestore.collection("lokasjoner");
  const zoom = 12;
  const imageSize = "300x400";
  const coordinates = `${longitude},${latitude}`;
  const imageUrl = `https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/${coordinates},${zoom},0,0/${imageSize}?access_token=${mapToken}`;

  const geoRes = await fetch(
    `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`,
  );
  const geoData = await geoRes.json();
  const countryCode = geoData.countryCode;

  const countryRes = await fetch(
    `https://api.restcountries.com/countries/v5/codes.alpha_2?q=${countryCode}`,
    { headers: { 'Authorization': 'Bearer rc_live_9cb05ee0d48b43b5b21d1841768ce7bc' } }
  );

  const jsonCountryRes = await countryRes.json();

  const currencyCode = jsonCountryRes.data.objects[0]?.currencies[0].code;
  const lang = languagesShortened[countryCode] || "nb_NO";
  const currency = currencyCode ? currencyCode : "NOK";

  const isStudent = !!userInformation?.studentOrganisasjon;

  const googleNumReviews =
    !isStudent && googleBusinessDetails?.user_ratings_total
      ? googleBusinessDetails.user_ratings_total
      : null;

  const googleRating =
    !isStudent && googleBusinessDetails?.rating
      ? googleBusinessDetails.rating
      : null;

  const googleId =
    !isStudent && googleBusinessDetails?.place_id
      ? googleBusinessDetails.place_id
      : null;

  const googleLink =
    !isStudent && googleBusinessDetails?.url ? googleBusinessDetails.url : null;

  const googleAdress = googleBusinessDetails?.formatted_address
    ? googleBusinessDetails.formatted_address
    : null;

  const googleWebsite = googleBusinessDetails?.website
    ? googleBusinessDetails.website
    : null;

  const googleBusinessStatus = googleBusinessDetails?.business_status
    ? googleBusinessDetails.business_status
    : null;

  // Permanent, unique slug for SEO-friendly place URLs — generated once
  // at creation and never regenerated on a later rename (see src/utils/slug.js).
  const newLocationSlug = await generateUniqueSlug(locationName);

  // First create the Firestore doc so we can use its id for the image filename
  const newLocationRef = await geocollection.add({
    locationName: locationName,
    slug: newLocationSlug,
    active: false,
    lat: latitude,
    lng: longitude,
    menus: [],
    mapImage: null,
    type: locationType,
    secondaryType: locationSecondaryType === "" ? null : locationSecondaryType,
    googleNumReviews: googleNumReviews,
    googleRating: googleRating,
    googleId: googleId,
    googleLink: googleLink,
    googleBusinessStatus: googleBusinessStatus,
    adress: googleAdress,
    website: googleWebsite,
    currency: currency,
    lang: lang,
    owner: currentBusinessID,
    coordinates: new firebase.firestore.GeoPoint(latitude, longitude),
    regularOpeningHours: regularOpeningHours,
    regularClosingHours: regularClosingHours,
    irregularHoursBoolean: isStudent,
    irregularHoursText: isStudent ? " " : "",
    eventsAllwaysShown: isStudent,
    ...(extraFields ? extraFields(lang) : {}),
  });

  // Fetch image and upload to Firebase Storage
  const response = await fetch(imageUrl);
  const blob = await response.blob();

  const storage = getStorage();
  const imageRef = ref(storage, `locationMapImages/${newLocationRef.id}.png`);

  await uploadBytes(imageRef, blob, {
    contentType: "image/png",
  });

  const downloadURL = await getDownloadURL(imageRef);

  // Update Firestore doc with the real storage URL
  const docRef = doc(db, "lokasjoner", newLocationRef.id);

  await updateDoc(docRef, {
    mapImage: downloadURL,
  });

  if (
    googleNumReviews !== null &&
    googleRating !== null &&
    googleId !== null &&
    googleLink !== null &&
    googleBusinessDetails !== null
  ) {
    const subcollectionRef = collection(docRef, "googleRating");
    const dayDocumentRef = doc(subcollectionRef, "googleRating");

    await setDoc(dayDocumentRef, {
      reviews: googleBusinessDetails.reviews,
      date: new Date(),
    });
  }

  await updateDoc(userInfo, {
    menus: arrayUnion(newLocationRef.id),
  });

  return { id: newLocationRef.id, lang, currency };
}
